import { ErrorMessage } from "@hookform/error-message";
import { FormRow } from "components/ui/form";
import Row from "components/ui/layout/Row";
import { motion } from "framer-motion";
import type {
  PlayerUser,
  Player,
  SimulatorReservation,
  LockerUserDto,
  RoundConfiguration,
  Option,
} from "Models";
import { useSelector } from "react-redux";
import { getSimulatorBySimId } from "modules/simulator/simulatorSelectors";
import type { RootState } from "StoreTypes";
import { Controller, useFormContext } from "react-hook-form";
import Select, { type SingleValue } from "react-select";
import { RoundType, UnityGames } from "types/enums";
import { LiaGolfBallSolid } from "react-icons/lia";
import { roundConfigEnumsAsReactSelectOptions } from "utils/enums/enumHelper";
import { customStyles } from "utils/ui/uiHelpers";
import FormattedUserSearchOption from "./FormattedUserSearchOption";
import QuickPlayerMenu from "./QuickPlayerMenu";
import RangeSpecificForm from "./RangeSpecificForm";
import RoundSpecificForm from "./RoundSpecificForm";
import TimerSelection from "./TimerSelection";
import tailwind from "./StartAdHocRoundContainer.styles";
import AsyncSelect from "react-select/async";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { showCustomNotification } from "utils/notifications/notificationHelpers";
import {
  ArrayHelper,
  sortStringArrayByProperty,
} from "utils/arrays/arrayHelpers";
import { debouncedLoadOptions } from "utils/userSearch/userSearchHelpers";
import { useFacilityDefaultRoundSettings } from "modules/default-round-settings";
import { useGetCurrentLocalFacilityId } from "modules/facility/facilityHooks";
import { FaPlus, FaMinus, FaRegClock } from "react-icons/fa";
import type { SendRoundRangeToSimInfo } from "Messages";
import {
  useInstalledFacilityUnityGames,
  useInstalledSimulatorUnityGames,
} from "modules/simulator/simulatorHooks";
import LockerRoomIcon from "components/svg/LockerRoomIcon";
import TodayIcon from "components/svg/TodayIcon";
import RoundConfigurationSelect from "./RoundConfigurationSelect";
import CreateLockerUserForm from "./CreateLockerUserForm";
import { first } from "@/utils/object";

const StartGameForm: React.FC<StartGameFormProps> = (props) => {
  const { unityGames } = useFlags();

  const localStyles = {
    ...customStyles, // your shared styles
    option: (provided: any, state: any) => {
      // start from whatever the shared style would return
      const base = customStyles.option
        ? customStyles.option(provided, state)
        : provided;

      return {
        ...base,
        cursor: "pointer",
        // keep selected color as-is; add a hover/focus background for others
        backgroundColor: state.isSelected
          ? base.backgroundColor
          : state.isFocused
            ? "#edf2f7" // Tailwind gray-200
            : base.backgroundColor,
      };
    },
  };

  const currentFacilityId = useGetCurrentLocalFacilityId();
  const simulator = useSelector((state: RootState) =>
    getSimulatorBySimId(state, { simId: props.simulatorId || "" }),
  );

  const { control, formState, watch, setValue } = useFormContext();
  const {
    simulatorReservation,
    simulatorId,
    queueItem,
    selectedQueueItem,
    callback,
    allowCreateLockerUser,
    allowNoPuttMode,
    allowPlayerProfileTeeType,
  } = props;

  const unityGameFilters = [
    { label: "All Games", value: "" },
    {
      label: "Ball Striking Games",
      value: "Ball Striking Games",
    },
    {
      label: "Bar and Arcade Games",
      value: "Bar and Arcade Games",
    },
    { label: "Mini Golf Games", value: "Mini Golf Games" },
    { label: "Multi-Sport Games", value: "Multi-Sport Games" },
  ];

  const [selectedSavedRound, setSavedRound] = useState<RoundConfiguration>();
  const [expandLockerUserMenu, setExpandLockerUserMenu] = useState(false);
  const [expandQuickPlayerMenu, setExpandQuickPlayerMenu] = useState(false);
  const [defaultRoundSettings] =
    useFacilityDefaultRoundSettings(currentFacilityId);

  const [facilityUnityGames] =
    useInstalledFacilityUnityGames(currentFacilityId);

  const installedSimulatorUnityGames = useInstalledSimulatorUnityGames(
    currentFacilityId,
    simulatorId || "",
  );

  const effectiveSimulatorId =
    simulatorReservation?.simulatorId ?? simulatorId ?? "";
  const hasSimulatorSelected = effectiveSimulatorId.trim() !== "";

  const roundTypeOptions = useMemo(() => {
    const filtered = roundConfigEnumsAsReactSelectOptions.gameType.filter(
      (gameType) => {
        if (unityGames) {
          return true;
        }
        return gameType.value !== RoundType.GAME;
      },
    );
    return filtered.map((opt) => {
      if (unityGames && opt.value === RoundType.GAME && !hasSimulatorSelected) {
        return {
          ...opt,
          label: "GAME - Select simulator to view Games",
        };
      }
      return opt;
    });
  }, [unityGames, hasSimulatorSelected]);

  const watchGameType = watch("roundType");

  const handlePlayerChange = (options: any) => {
    if (callback) callback(options);
    setValue("roundInfo.players", options);
  };

  const applyRoundConfiguration = useCallback(
    (selectedRound: RoundConfiguration | undefined, buildId: number = 0) => {
      if (selectedRound) {
        // Round settings
        setValue("autoContinue", selectedRound?.autoContinue);
        setValue("build", buildId);
        setValue("rangeTime", selectedRound?.rangeTime);
        setValue("roundType", selectedRound?.roundType);
        setValue("id", selectedRound?.id);
        // Round info settings
        setValue(
          "roundInfo.autoConcede",
          selectedRound?.roundInfo?.autoConcede,
        );
        setValue("roundInfo.autoDrop", selectedRound?.roundInfo?.autoDrop);
        setValue("roundInfo.autoGimmes", selectedRound?.roundInfo?.autoGimmes);
        setValue("roundInfo.courseName", selectedRound.roundInfo?.courseName);
        setValue(
          "roundInfo.gimmeDistance",
          selectedRound?.roundInfo?.gimmeDistance,
        );
        setValue(
          "roundInfo.groundConditions",
          selectedRound?.roundInfo?.groundConditions,
        );
        setValue(
          "roundInfo.handicapMode",
          selectedRound?.roundInfo?.handicapMode,
        );
        setValue(
          "roundInfo.handicapPercentage",
          selectedRound?.roundInfo?.handicapPercentage,
        );
        setValue(
          "roundInfo.courseRatingSlope",
          selectedRound?.roundInfo?.courseRatingSlope,
        );
        setValue("roundInfo.mensTee", selectedRound.roundInfo?.mensTee);
        setValue("roundInfo.mulligans", selectedRound?.roundInfo?.mulligans);
        setValue("roundInfo.pinType", selectedRound?.roundInfo?.pinType);
        setValue(
          "roundInfo.scoringType",
          selectedRound?.roundInfo?.scoringType,
        );
        setValue(
          "roundInfo.skyConditions",
          selectedRound?.roundInfo?.skyConditions,
        );
        setValue(
          "roundInfo.stimpSetting",
          selectedRound?.roundInfo?.stimpSetting,
        );
        setValue(
          "roundInfo.timeConditions",
          selectedRound?.roundInfo?.timeConditions,
        );
        setValue(
          "roundInfo.windDirection",
          selectedRound?.roundInfo?.windDirection,
        );
        setValue("roundInfo.windSpeed", selectedRound?.roundInfo?.windSpeed);
        setValue("roundInfo.womensTee", selectedRound.roundInfo?.womensTee);
        setValue(
          "roundInfo.usePlayersMemberTee",
          selectedRound.roundInfo?.usePlayersMemberTee,
        );

        // Stroke settings
        setValue(
          "roundInfo.strokeSettings.noPuttMode",
          selectedRound?.roundInfo?.strokeSettings?.noPuttMode,
        );
        setValue(
          "roundInfo.noPuttMode",
          selectedRound?.roundInfo?.strokeSettings?.noPuttMode,
        );

        // Match settings
        setValue(
          "roundInfo.matchSettings.scotchFoursome",
          selectedRound?.roundInfo?.matchSettings?.scotchFoursome,
        );

        // Holes
        setValue(
          "holes",
          ArrayHelper.convertBooleanToNumberArray(
            selectedRound.roundInfo?.holesSelected,
          ),
        );

        showCustomNotification(`${selectedRound?.name} has been loaded.`);
      } else {
        setValue("build", buildId);
        setValue("roundInfo.usePlayersMemberTee", false);
        setValue("roundInfo.usePlayersTee", false);
      }
    },
    [setValue],
  );

  useEffect(() => {
    const buildId = simulator?.caddyVersion || 0;
    applyRoundConfiguration(selectedSavedRound, buildId);
  }, [selectedSavedRound, applyRoundConfiguration, simulator?.caddyVersion]);

  useEffect(() => {
    if (simulatorReservation) {
      setValue("roundType", RoundType.ROUND);
    }
    if (selectedQueueItem) {
      setValue(
        "roundInfo.usePlayersMemberTee",
        selectedQueueItem?.roundConfiguration?.roundInfo?.usePlayersMemberTee,
      );
      setValue(
        "gameConfiguration.gameFilter",
        installedSimulatorUnityGames
          ?.find(
            (game) =>
              game.unityGameId ===
              Number(selectedQueueItem?.gameConfiguration?.game),
          )
          ?.gameTypes.find(
            (gameType) =>
              gameType.name ===
                selectedQueueItem?.gameConfiguration?.gameFilter || "",
          )?.name ||
          installedSimulatorUnityGames?.find(
            (game) =>
              game.unityGameId ===
              Number(selectedQueueItem?.gameConfiguration?.game),
          )?.gameTypes[0]?.name,
      );
      setValue(
        "gameConfiguration.game",
        Number(selectedQueueItem?.gameConfiguration?.game),
      );
      if (selectedQueueItem?.teams?.length) {
        selectedQueueItem?.teams?.forEach((team, index) => {
          setValue(`teams.${index}`, team);
        });
      }
    }
  }, [
    simulatorReservation,
    setValue,
    selectedQueueItem,
    installedSimulatorUnityGames,
  ]);

  return (
    <div className="mb-4">
      <Row cssClasses="mt-4">
        <span className={`${tailwind.span}`}>
          <LockerRoomIcon class="size-4 mr-1" /> Add Players{" "}
          {simulatorReservation &&
            watch("roundInfo.players")?.length +
              "/" +
              simulatorReservation?.numberInParty}
        </span>
      </Row>
      <Row cssClasses="mt-2">
        <Controller
          name="roundInfo.players"
          rules={{
            validate: {
              required: () => {
                if (watch("roundInfo.game")) {
                  if (
                    installedSimulatorUnityGames.find(
                      (game) => game.unityGameId === watch("roundInfo.game"),
                    )?.doesntUsePlayers === false &&
                    watch("roundInfo.players")?.length === 0
                  )
                    return "At least one player is required";
                } else {
                  if (watch("roundInfo.players")?.length === 0)
                    return "At least one player is required";
                }
              },
            },
          }}
          control={control}
          defaultValue={props.players || []}
          render={({ field }) => (
            <AsyncSelect
              {...field}
              isMulti
              backspaceRemovesValue={false}
              noOptionsMessage={() =>
                "No players found. Try another search or click 'Create User' to create a new player."
              }
              loadOptions={(query) => debouncedLoadOptions(query)}
              getOptionValue={(player: PlayerUser) => JSON.stringify(player)}
              formatOptionLabel={(option, { context }) => {
                return (
                  <FormattedUserSearchOption
                    value={option}
                    context={context}
                    currentFacilityId={currentFacilityId}
                  />
                );
              }}
              data-testid="roundInfo.players"
              styles={localStyles}
              placeholder={"Start typing to search..."}
              id="startGameAddPlayers"
              inputId="startGameAddPlayers"
              onChange={(options) => handlePlayerChange(options)}
              className="dark:text-white"
              classNames={{
                option: ({
                  isFocused,
                  isSelected,
                }: {
                  isFocused: boolean;
                  isSelected: boolean;
                }) =>
                  `px-2 py-1 rounded cursor-pointer ${
                    isFocused && !isSelected
                      ? "bg-gray-200 dark:bg-gray-600"
                      : ""
                  }`,
                menuList: () => "py-1",
              }}
            />
          )}
        />
        <small
          className={`pt-2 text-gray-400 dark:text-gray-200 text-xs inline-block`}
        >
          Search by name, email, phone or Locker username. Click &apos;Create
          User&apos; to create a new player.
        </small>
      </Row>
      <Row cssClasses="mt-2">
        <ErrorMessage
          errors={formState.errors}
          name="roundInfo.players"
          render={({ message }) => (
            <div className="text-sm text-red-500">{message}</div>
          )}
        />
      </Row>
      <Row cssClasses="flex-row py-2 mb-2">
        {allowCreateLockerUser && (
          <span
            data-testid="createLockerUserLink"
            onClick={() => {
              setExpandLockerUserMenu(!expandLockerUserMenu);
              setExpandQuickPlayerMenu(false);
            }}
            className={`hover:cursor-pointer text-xs px-4 py-2 poppins rounded-xl ${
              expandLockerUserMenu
                ? " bg-white dark:bg-zinc-600 border-gray-300 border text-black dark:text-white"
                : " bg-brandGreen dark:bg-brandGreen hover:bg-brandYellow text-white hover:text-black dark:text-black"
            } `}
          >
            {expandLockerUserMenu ? (
              <FaMinus className="text-black w-3 h-3 pb-0.5" />
            ) : (
              <FaPlus className=" w-3 h-3 pb-0.5" />
            )}
            <span className={`${tailwind.quickPlayer} pr-2 text-xs`}>
              Create User
            </span>{" "}
          </span>
        )}

        {allowCreateLockerUser && expandLockerUserMenu && (
          <div className="negativeMarginOutside">
            <motion.div className="px-4 py-4 border-white bg-gray-100 dark:bg-zinc-600 mt-6 negativeMarginInside">
              <CreateLockerUserForm
                showHideComponentCallback={setExpandLockerUserMenu}
                returnGuidOnly={false}
                returnLockerUserGuidCallback={(player: LockerUserDto) => {
                  handlePlayerChange([
                    ...watch("roundInfo.players"),
                    {
                      displayName: player.firstName + " " + player.lastName,
                      email: player.email,
                      userGuid: player.userId,
                      userName: player.userName,
                      phoneNumber: player.phoneNumber?.number,
                      agHandicap: player.handicap,
                    },
                  ]);
                }}
              />

              <div
                data-testid="quickPlayerLink"
                onClick={() => {
                  setExpandLockerUserMenu(false);
                  setExpandQuickPlayerMenu(!expandQuickPlayerMenu);
                }}
                className={`hover:cursor-pointer text-xs px-4 py-2 poppins rounded-lg ${
                  expandQuickPlayerMenu
                    ? " bg-white dark:bg-zinc-600 text-center border-gray-300 border text-black dark:text-white rounded-xl"
                    : ` hover:border-black text-center bg-white text-gray-500 text-normal border border-gray-200 rounded-xl dark:text-white ${
                        allowCreateLockerUser ? "mt-4 w-1/2" : "mt-4 w-1/2"
                      } `
                } `}
              >
                {expandQuickPlayerMenu ? (
                  <FaMinus className="text-black w-3 h-3 pb-0.5" />
                ) : (
                  <FaPlus className="text-gray-800 w-3 h-3 pb-0.5" />
                )}
                <span
                  className={`${tailwind.quickPlayer} pr-2 text-xs dark:text-zinc-800`}
                >
                  Add Quick Player
                </span>{" "}
              </div>
            </motion.div>
          </div>
        )}
        {expandQuickPlayerMenu && (
          <div className="negativeMarginOutside">
            <motion.div className="border-white pb-2 bg-gray-100 dark:bg-zinc-600 negativeMarginInside">
              <QuickPlayerMenu
                setExpandQuickPlayerMenu={setExpandQuickPlayerMenu}
              />
            </motion.div>
          </div>
        )}
      </Row>

      <FormRow>
        <span className={tailwind.span}>
          <TodayIcon class="size-4 mr-1" /> Select Round, Range or Game
        </span>
      </FormRow>

      <Row cssClasses="pt-2">
        <Controller
          name="roundType"
          rules={{ required: "Round type is required" }}
          control={control}
          render={({ field }) => (
            <Select
              data-testid="roundType-select"
              isSearchable={false}
              closeMenuOnSelect={true}
              isMulti={false}
              options={roundTypeOptions}
              isOptionDisabled={(option: Option<RoundType>) => {
                if (!unityGames || hasSimulatorSelected) {
                  return false;
                }
                return option.value === RoundType.GAME;
              }}
              value={
                roundTypeOptions.find((c) => c.value === field.value) ?? null
              }
              onChange={(val: SingleValue<Option<RoundType>>) => {
                if (!val) {
                  return;
                }
                field.onChange(val.value);
              }}
              styles={customStyles}
              menuPlacement="auto"
            />
          )}
        />
        <ErrorMessage
          errors={formState.errors}
          name="roundType"
          render={({ message }) => (
            <div className={tailwind.errorMsg}>{message}</div>
          )}
        />
        <small
          className={`pt-2 text-gray-400 dark:text-gray-200 text-xs inline-block`}
        >
          Start a new round, range or ball striking game
        </small>
      </Row>
      {watchGameType === RoundType.GAME && (
        <>
          <FormRow>
            <span className={tailwind.span}>Select Game Type</span>
          </FormRow>
          <FormRow>
            <Controller
              name="gameConfiguration.gameFilter"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  closeMenuOnSelect={true}
                  isMulti={false}
                  options={unityGameFilters}
                  value={unityGameFilters.find(
                    (c) => c.value === watch("gameConfiguration.gameFilter"),
                  )}
                  onChange={(
                    val: SingleValue<{ label: string; value: string }>,
                  ) => {
                    if (!val) {
                      return;
                    }
                    field.onChange(val.value);
                    setValue("gameConfiguration.game", "");
                  }}
                  styles={customStyles}
                  menuPlacement="auto"
                />
              )}
            />
          </FormRow>
          <FormRow>
            <span className={tailwind.span}>Select Game</span>
          </FormRow>
          <FormRow>
            <Controller
              name="gameConfiguration.game"
              control={control}
              defaultValue={watch("gameConfiguration.game")}
              render={({ field }) => (
                <Select
                  {...field}
                  closeMenuOnSelect={true}
                  isMulti={false}
                  options={sortStringArrayByProperty(
                    installedSimulatorUnityGames,
                    "name",
                    true,
                  )
                    .filter((game) =>
                      game.gameTypes.some((gameType) =>
                        gameType.name?.includes(
                          watch("gameConfiguration.gameFilter"),
                        ),
                      ),
                    )
                    .map((game) => ({
                      label: game.name,
                      value: game.unityGameId,
                    }))}
                  value={installedSimulatorUnityGames
                    .filter((c) => c.unityGameId === field.value)
                    .map((game) => ({
                      label: game.name,
                      value: game.unityGameId,
                    }))}
                  onChange={(val: SingleValue<Option<UnityGames>>) => {
                    if (!val) {
                      return;
                    }
                    field.onChange(val.value);
                    setValue("gameConfiguration.name", val.label);
                  }}
                  styles={customStyles}
                  menuPlacement="auto"
                />
              )}
            />
          </FormRow>
          {first(facilityUnityGames?.simulators)?.games.find(
            (game) => game.unityGameId === watch("gameConfiguration.game"),
          )?.optionalMultiSport ? (
            <FormRow>
              <Controller
                name="gameConfiguration.useMultiSport"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="checkbox"
                    value="true"
                    className="mr-2 rounded border border-gray-400 text-brandGreen focus:ring-brandGreen"
                  />
                )}
              />
              <div className="inline pt-1 text-xs poppins">
                Use multisport camera
              </div>
            </FormRow>
          ) : null}
        </>
      )}
      {watchGameType === RoundType.RANGE ? <RangeSpecificForm /> : null}
      {!simulatorReservation && !queueItem && watchGameType !== undefined ? (
        <>
          <div className="bg-gray-100 dark:bg-zinc-800 -mx-12 px-12 mt-6 pb-6">
            <FormRow fontSize="py-2">
              <FaRegClock className="size-4 mr-2" />
              <span className={`${tailwind.span} pb-2 inline`}>
                {`How many minutes do you want for the ${RoundType[
                  watchGameType
                ]?.toLowerCase()} timer?`}
              </span>
              <div className="pt-2">
                <TimerSelection />
              </div>
            </FormRow>
          </div>
        </>
      ) : null}
      {watchGameType === RoundType.ROUND ? (
        <>
          <Row cssClasses="mt-4 pt-4">
            <RoundConfigurationSelect
              onChange={setSavedRound}
              selectedScoringType={watchGameType}
            />
          </Row>
          <div className="mt-6 flex items-center">
            <span className={`${tailwind.span} flex items-center`}>
              <LiaGolfBallSolid className="size-5 mr-1" />
              Select Course and Holes
            </span>
          </div>
          <RoundSpecificForm
            simulatorId={simulatorReservation?.simulatorId || simulatorId}
            defaultRoundSettings={defaultRoundSettings}
            allowNoPuttMode={allowNoPuttMode}
            allowPlayerProfileTeeType={allowPlayerProfileTeeType}
          />
        </>
      ) : null}
    </div>
  );
};
export default StartGameForm;

type StartGameFormProps = {
  simulatorReservation?: SendRoundRangeToSimInfo;
  selectedQueueItem?: SimulatorReservation;
  simulatorId?: string;
  queueItem?: boolean;
  callback?: React.Dispatch<React.SetStateAction<any>>;
  players?: Player[];
  allowCreateLockerUser?: boolean;
  allowNoPuttMode?: boolean;
  allowPlayerProfileTeeType?: boolean;
};
